import {
  getWeaknessCatalogEntryById,
  resolveWeaknessCatalogEntry,
  type InternalNetworkAssessmentActionDto,
  type InternalNetworkAssessmentEntityActionGraphDto,
  type InternalNetworkAssessmentEntityDto,
} from '@terra/shared-contracts-internal-network';
import {
  InfraImpactSchema,
  type InfraImpact,
} from '@terra/shared-schemas-orchestrator';
import type { AttackPathEventNodeRole } from './attack-path-event-node.types';

export type AssessmentEntityActionAchievedImpact = {
  infraImpact: InfraImpact;
  pathEntityIds: string[];
  focusEntityId: string | null;
};

const DOMAIN_COMPROMISE_GOAL_KEY = 'domain-compromise';

export const resolveGoalInfraImpacts = (
  entity: Pick<InternalNetworkAssessmentEntityDto, 'kind' | 'goalKey'>,
): InfraImpact[] => {
  if (entity.kind !== 'GOAL' || entity.goalKey == null) {
    return [];
  }

  if (entity.goalKey === DOMAIN_COMPROMISE_GOAL_KEY) {
    return ['DOMAIN_COMPROMISE'];
  }

  const parsed = InfraImpactSchema.safeParse(entity.goalKey);

  return parsed.success ? [parsed.data] : [];
};

const parseAchievedImpacts = (
  impactsAchieved: readonly string[],
): InfraImpact[] =>
  impactsAchieved.flatMap((impact) => {
    const parsed = InfraImpactSchema.safeParse(impact);

    return parsed.success ? [parsed.data] : [];
  });

const resolveActionInfraImpact = (
  action: InternalNetworkAssessmentActionDto,
): InfraImpact | null => {
  if (action.weaknessId) {
    const byId = getWeaknessCatalogEntryById(action.weaknessId);

    if (byId?.infraImpact) {
      return byId.infraImpact;
    }
  }

  return (
    resolveWeaknessCatalogEntry({
      technique: action.technique ?? action.mitreTechniqueId,
    })?.infraImpact ?? null
  );
};

const collectAncestorEntityIds = ({
  graph,
  terminalIds,
}: {
  graph: InternalNetworkAssessmentEntityActionGraphDto;
  terminalIds: readonly string[];
}): string[] => {
  const inbound = new Map(
    graph.actions.map((action) => [
      action.targetEntityId,
      action.sourceEntityId,
    ]),
  );
  const path = new Set<string>();

  for (const terminalId of terminalIds) {
    const seen = new Set<string>();
    let current: string | undefined = terminalId;

    while (current && !seen.has(current)) {
      seen.add(current);
      path.add(current);
      current = inbound.get(current);
    }
  }

  return [...path];
};

const resolveImpactTerminalEntityIds = ({
  graph,
  infraImpact,
}: {
  graph: InternalNetworkAssessmentEntityActionGraphDto;
  infraImpact: InfraImpact;
}): string[] => {
  const goalTerminals = graph.entities
    .filter(
      (entity) =>
        entity.status === 'SUCCEEDED' &&
        resolveGoalInfraImpacts(entity).includes(infraImpact),
    )
    .map((entity) => entity.id);

  if (goalTerminals.length > 0) {
    return goalTerminals;
  }

  const hopTerminals = [
    ...new Set(
      graph.actions
        .filter(
          (action) =>
            action.status === 'SUCCEEDED' &&
            resolveActionInfraImpact(action) === infraImpact,
        )
        .map((action) => action.targetEntityId),
    ),
  ];

  if (hopTerminals.length > 0) {
    return hopTerminals;
  }

  if (infraImpact === 'DOMAIN_COMPROMISE') {
    return graph.entities
      .filter(
        (entity) => entity.kind === 'GOAL' && entity.status === 'SUCCEEDED',
      )
      .map((entity) => entity.id);
  }

  return graph.entities
    .filter(
      (entity) => entity.status === 'SUCCEEDED' || entity.status === 'OWNED',
    )
    .map((entity) => entity.id);
};

export const resolveAssessmentEntityActionAchievedImpacts = (
  graph: InternalNetworkAssessmentEntityActionGraphDto,
): AssessmentEntityActionAchievedImpact[] =>
  parseAchievedImpacts(graph.impactsAchieved).map((infraImpact) => {
    const terminalIds = resolveImpactTerminalEntityIds({
      graph,
      infraImpact,
    });
    const pathEntityIds = collectAncestorEntityIds({
      graph,
      terminalIds,
    });

    return {
      infraImpact,
      pathEntityIds,
      focusEntityId: terminalIds.at(-1) ?? null,
    };
  });

export const resolveAssessmentImpactBrightNodeIds = ({
  nodes,
  pathEntityIds,
}: {
  nodes: ReadonlyArray<{
    id: string;
    data: {
      role?: AttackPathEventNodeRole;
      signalId?: string | null;
    };
  }>;
  pathEntityIds: ReadonlyArray<string>;
}): Set<string> => {
  const bright = new Set(pathEntityIds);
  const pathSignals = new Set(
    nodes
      .filter((node) => pathEntityIds.includes(node.id) && node.data.signalId)
      .map((node) => node.data.signalId as string),
  );

  nodes.forEach((node) => {
    if (
      node.data.signalId != null &&
      pathSignals.has(node.data.signalId) &&
      (node.data.role === 'SIGNAL_EXECUTION' ||
        node.data.role === 'CAMPAIGN_FRAME')
    ) {
      bright.add(node.id);
    }
  });

  return bright;
};
